'use client';

import Modal from '@/components/ui/modals';
import { useTranslations, useLocale } from 'next-intl';
import ShippingMethod from './shipping-method/ShippingMethod';
import DeliveryAddressModal from './shipping-address/delivery-address-modal';
import { useState } from 'react';
import AddAddressOptionsModal from './shipping-address/add-address-options-modal';
import AddEditAddressModal from './shipping-address/add-edit-address-modal';
import ArrowBackIcon from '../../icons/arrow-back';
import useShippingInfoStore from '@/store/shipping-info.store';
import NearestBranches from './shipping-method/NearestBranches';
import UnavailableItemsBranches from './shipping-method/UnavailableItemsBranches';

import { SHIPPING_INFO_MODALS_NAMES } from '@/constants';

export default function ShippingInfoModal() {
  const {
    openShippingInfoModal,
    setIsOpenShippingInfoModal,
    shippingMethod,
    setShippingMethod,
    setShippingAddress,
    shippingAddress,
    activeStep,
    selectedAddressToEdit,
    setActiveStep,
    setSelectedAddressToEdit,
    refetchAddressesAfterAddEdit: refetchAddresses,
    unavailableItems,
    availableBranches,
    isLoadingAvailableBranches,
  } = useShippingInfoStore();

  const locale = useLocale();
  const t = useTranslations('modals');

  const [addressType, setAddressType] = useState(
    selectedAddressToEdit?.custom_attributes?.find(
      (e) => e?.attribute_code === 'address_type'
    )?.value
  );

  const handleOnClose = () => {
    setSelectedAddressToEdit({});

    if (shippingMethod === SHIPPING_INFO_MODALS_NAMES.HOME_DELIVERY) {
      setActiveStep(SHIPPING_INFO_MODALS_NAMES.DELIVERY_ADDRESS);
    } else if (shippingMethod === SHIPPING_INFO_MODALS_NAMES.STORE_PICKUP) {
      setActiveStep(SHIPPING_INFO_MODALS_NAMES.CHOOSE_BRANCH);
    } else {
      setActiveStep(SHIPPING_INFO_MODALS_NAMES.SHIPPING_METHOD);
    }

    setIsOpenShippingInfoModal(false);
    setAddressType('');
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case SHIPPING_INFO_MODALS_NAMES.SHIPPING_METHOD:
        return (
          <ShippingMethod
            {...{
              shippingMethod,
              setShippingMethod,
              setIsOpenShippingInfoModal,
              setModalStep: setActiveStep,
              setShippingAddress,
            }}
          />
        );
      case SHIPPING_INFO_MODALS_NAMES.DELIVERY_ADDRESS:
        return (
          <DeliveryAddressModal
            {...{
              modalStep: activeStep,
              setModalStep: setActiveStep,
              setShippingMethod,
              handleOnClose,
              setShippingAddress,
              shippingAddress,
              setSelectedAddress: setSelectedAddressToEdit,
              setAddressType,
            }}
          />
        );
      case SHIPPING_INFO_MODALS_NAMES.ADD_ADDRESS_OPTIONS:
        return (
          <AddAddressOptionsModal
            {...{
              setModalStep: setActiveStep,
              handleOnClose,
              setAddressType,
              addressType,
              setSelectedAddress: setSelectedAddressToEdit,
            }}
          />
        );
      case SHIPPING_INFO_MODALS_NAMES.ADD_EDIT_ADDRESS:
        return (
          <AddEditAddressModal
            {...{
              modalStep: activeStep,
              setModalStep: setActiveStep,
              handleOnClose,
              addressType,
              selectedAddress: selectedAddressToEdit,
              setShippingAddress,
              refetchAddresses,
            }}
          />
        );

      case SHIPPING_INFO_MODALS_NAMES.CHOOSE_BRANCH:
        return (
          <NearestBranches
            {...{
              handleOnClose,
              setSelectedBranch: setShippingAddress,
              selectedBranch: shippingAddress,
              setModalStep: setActiveStep,
              setShippingMethod,
            }}
          />
        );
      case SHIPPING_INFO_MODALS_NAMES.UNAVAILABLE_ITEMS_BRANCHES:
        return (
          <UnavailableItemsBranches
            {...{
              handleOnClose,
              setSelectedBranch: setShippingAddress,
              selectedBranch: shippingAddress,
              availableBranches: availableBranches || [],
              unavailableItems: unavailableItems || [],
              isLoadingBranches: isLoadingAvailableBranches || false,
            }}
          />
        );
      default:
        return null;
    }
  };

  return (
    openShippingInfoModal && (
      <Modal
        maxWidth='max-w-[96%] lg:max-w-[645px]'
        isOpen={openShippingInfoModal}
        onClose={handleOnClose}
        headerTitleClassName='max-lg:!p-2 !px-4 !py-3 max-lg:text-sm'
        title={
          <div className='flex items-center gap-4 max-sm:text-sm'>
            {(activeStep === SHIPPING_INFO_MODALS_NAMES.ADD_ADDRESS_OPTIONS ||
              activeStep === SHIPPING_INFO_MODALS_NAMES.ADD_EDIT_ADDRESS) && (
              <button
                type='button'
                onClick={() =>
                  setActiveStep(SHIPPING_INFO_MODALS_NAMES.DELIVERY_ADDRESS)
                }
              >
                <ArrowBackIcon
                  className={`${locale != 'en' && 'rotate-180'}`}
                />
              </button>
            )}
            <span>
              {activeStep ===
              SHIPPING_INFO_MODALS_NAMES.UNAVAILABLE_ITEMS_BRANCHES
                ? t('unavailable_items_title')
                : t(
                    activeStep ===
                      SHIPPING_INFO_MODALS_NAMES.ADD_ADDRESS_OPTIONS ||
                      activeStep === SHIPPING_INFO_MODALS_NAMES.ADD_EDIT_ADDRESS
                      ? 'Add / Edit Address'
                      : 'Select your shipping method'
                  )}
            </span>
          </div>
        }
        titleClassName='font-bold text-xl'
      >
        {renderStepContent()}
      </Modal>
    )
  );
}
